/** 
 Copyright Highway9 Networks Inc. 
 */ 
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { userService } from "~/services";
import type { UserInfo } from "~/types/user";
import type { RootState } from "..";
import { createSocketPatchReducer } from "../events/socketPatch";

type initState = {
  open: boolean;
  selectedId: string | null;
  data: EntityState<UserInfo>;
}

export const usersAdapter = createEntityAdapter<UserInfo>();

const initialState: initState = {
  open: false,
  selectedId: null,
  data: usersAdapter.getInitialState(),
}

export const fetchUsers = createAsyncThunk("user/fetchUsers", async () => {
  return await userService.getAllUser();
})

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSelectedId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload ?? null;
    },
    applySocketPatches: createSocketPatchReducer(usersAdapter, (s) => s.data),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      usersAdapter.setAll(state.data, action.payload);

      // Reset selection when selected user no longer exists.
      if (state.selectedId && !state.data.entities[state.selectedId]) {
        state.selectedId = null;
      }
    })
      .addCase(fetchUsers.rejected, (state, action) => {
        console.log(action.error.message);
        usersAdapter.removeAll(state.data);
        state.selectedId = null;
      })
  }
});

export const userActions = userSlice.actions;

export default userSlice.reducer;

const userEntitySelectors = usersAdapter.getSelectors((state: RootState) => state.user.data);

export const userData = userEntitySelectors.selectAll;
export const userEntities = userEntitySelectors.selectEntities;
export const userState = createSelector(
  [(state: RootState) => state.user.selectedId, userEntities],
  (selectedId, entities): UserInfo | null => selectedId ? entities[selectedId] ?? null : null,
);

